import { useQueryClient } from '@tanstack/react-query';
import { cn, VIconActionButton } from '@vrzl/ui';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { eventKeys } from '@/domains/events/api/keys';

import { useMe } from '../api/me';
import { useTriggerSync } from '../api/mutations';
import { syncKeys, useLatestSyncRun } from '../api/queries';
import type { SyncRun } from '../api/types';
import { useCanManualSync } from '../hooks/useCanManualSync';

const STALE_RUNNING_AFTER_MS = 10 * 60_000;
const REFRESH_AFTER_TRIGGER_MS = 10_000;

function isStaleRunning(run: SyncRun | null | undefined): boolean {
  if (run?.status !== 'RUNNING') return false;
  return Date.now() - new Date(run.startedOn).getTime() > STALE_RUNNING_AFTER_MS;
}

function formatTooltip(run: SyncRun | null | undefined, isPending: boolean): string {
  if (isPending) return 'Sync wordt gestart…';
  if (!run) return 'Sync handmatig starten';

  if (run.status === 'RUNNING') {
    const started = new Date(run.startedOn).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit',
    });
    if (isStaleRunning(run)) {
      return `Sync loopt sinds ${started} — mogelijk vastgelopen (>10 min). Klik om opnieuw te starten.`;
    }
    return `Sync loopt sinds ${started}…`;
  }

  const finishedAt = run.completedOn
    ? new Date(run.completedOn).toLocaleString('nl-NL', {
        dateStyle: 'short',
        timeStyle: 'short',
      })
    : '—';

  if (run.status === 'FAILED') {
    return `Laatste sync: FAILED (${finishedAt}) — ${run.error ?? 'onbekende fout'}\n\nKlik om opnieuw te proberen.`;
  }

  return `Laatste sync: ${finishedAt}\nCases: ${run.casesUpserted} · Documenten: ${run.documentsUpserted} · Blobs: ${run.blobsSynced}/${run.blobsReconciled} (${run.blobsFailed} mislukt)\n\nKlik om opnieuw te starten.`;
}

export function ManualSyncButton() {
  const canSync = useCanManualSync();
  const qc = useQueryClient();
  const { data: me } = useMe();
  const { data: latest } = useLatestSyncRun();
  const trigger = useTriggerSync();
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  if (!canSync) return null;

  const stale = isStaleRunning(latest);
  const isActiveRunning = latest?.status === 'RUNNING' && !stale;
  const isFailed = (latest?.status === 'FAILED' || stale) && !trigger.isPending;
  const disabled = isActiveRunning || trigger.isPending || !me;
  const Icon = isFailed ? AlertCircle : RefreshCw;

  const handleClick = () => {
    if (!me) return;
    trigger.mutate(undefined, {
      onSuccess: () => {
        if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = setTimeout(() => {
          qc.invalidateQueries({ queryKey: eventKeys.lists() });
          qc.invalidateQueries({ queryKey: syncKeys.latest() });
          refreshTimeoutRef.current = null;
        }, REFRESH_AFTER_TRIGGER_MS);
      },
    });
  };

  return (
    <VIconActionButton
      icon={Icon}
      label="Sync handmatig starten"
      tooltip={formatTooltip(latest, trigger.isPending)}
      onClick={handleClick}
      disabled={disabled}
      iconClassName={cn(disabled && 'animate-spin', isFailed && 'text-error-600')}
    />
  );
}
